import React, { useState, useCallback, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "../hooks/GoogleMapsProvider";

const mapContainerStyle = { width: "100%", height: "450px", borderRadius: "0.5rem" };
const defaultCenter = { lat: 15.4909, lng: 73.8278 }; // Goa fallback

const LocationPickerModal = ({ show, onHide, onConfirm, initialLat, initialLng }) => {
    const { isLoaded, loadError } = useGoogleMaps();

    const [markerPos, setMarkerPos] = useState(
        initialLat && initialLng
            ? { lat: parseFloat(initialLat), lng: parseFloat(initialLng) }
            : defaultCenter
    );
    const [address, setAddress] = useState("");
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

    const reverseGeocode = (lat, lng) => {
        if (!window.google) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) setAddress(results[0].formatted_address);
        });
    };

    const updatePosition = (lat, lng) => {
        setMarkerPos({ lat, lng });
        reverseGeocode(lat, lng);
    };

    const onMarkerDragEnd = (e) => updatePosition(e.latLng.lat(), e.latLng.lng());
    const onMapClick = (e) => updatePosition(e.latLng.lat(), e.latLng.lng());

    const onPlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMarkerPos({ lat, lng });
        setAddress(place.formatted_address || "");
        mapRef.current?.panTo({ lat, lng });
        mapRef.current?.setZoom(16);
    };

    const handleConfirm = () => {
        onConfirm({ lat: markerPos.lat, lng: markerPos.lng, address });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-6 fw-bold">Pick Outlet Location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!isLoaded ? (
                    <div className="text-center py-5">Loading map...</div>
                ) : (
                    <>
                        <Autocomplete onLoad={(ac) => (autocompleteRef.current = ac)} onPlaceChanged={onPlaceChanged}>
                            <Form.Control type="text" size="sm" placeholder="Search for a place or address" className="mb-2" />
                        </Autocomplete>

                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={markerPos}
                            zoom={15}
                            onLoad={onMapLoad}
                            onClick={onMapClick}
                        >
                            <Marker position={markerPos} draggable onDragEnd={onMarkerDragEnd} />
                        </GoogleMap>

                        <div className="mt-2 small text-muted">
                            {address && <div className="mb-1"><i className="ri-map-pin-line me-1"></i>{address}</div>}
                            <div>Lat: {markerPos.lat.toFixed(6)}, Lng: {markerPos.lng.toFixed(6)}</div>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!isLoaded}>
                    Use this Location
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LocationPickerModal;